import { DataEntry, PageLevel0Props } from './models';

import {
  AssemblyLevel0TopCarouselItemsCollectionArticleFragment,
  Level0CarouselTeaserFieldsFragment,
} from 'graphql/__generated/sdk';
import { URL_LANGUAGE_ENUM } from 'utils/constants';
import { generateEntrySlug } from 'utils/mappers';
import { BaseCardContent } from 'utils/models';
import {
  generateBaseCard,
  generateBillboard,
  generateHeroBanner,
  generateSeoMetadata,
  generateTable,
  getFirstContentItem,
} from 'utils/transformers';

function buildBaseCard(
  card:
    | AssemblyLevel0TopCarouselItemsCollectionArticleFragment
    | Level0CarouselTeaserFieldsFragment,
  language: URL_LANGUAGE_ENUM
): BaseCardContent {
  if (card.__typename === 'Article') {
    const url = generateEntrySlug(card, language);

    return generateBaseCard({
      title: card.title,
      url,
      image: getFirstContentItem(card.images)!,
      body: card.preamble || '',
      category: card.category || '',
    });
  }

  return generateBaseCard({
    title: card.title,
    url: card.teaserUrl || '',
    image: card.primaryMediaImage,
    body: card.richPreamble?.json || '',
    tagline: card.tagline || '',
  });
}

function prepareBaseCards(
  topCarouselItemsCollection: {
    items: (
      | AssemblyLevel0TopCarouselItemsCollectionArticleFragment
      | Level0CarouselTeaserFieldsFragment
    )[];
  },
  language: URL_LANGUAGE_ENUM
) {
  return topCarouselItemsCollection.items.map((item) => buildBaseCard(item, language));
}

export function getMappedPageContent(
  data: DataEntry,
  language: URL_LANGUAGE_ENUM
): PageLevel0Props {
  const [pageData] = data;
  const content = pageData.data.items[0];

  return {
    heroBanner: generateHeroBanner({
      title: content.heroBanner.title,
      preamble: content.heroBanner.richPreamble,
      image: content.heroBanner.primaryMediaImage,
      videoUrl: content.heroBanner.primaryMediaVideo?.video?.url,
      cta: getFirstContentItem(content.heroBanner.callToActionCollection),
    }),

    topCarousel: {
      title: content.topCarouselHeading,
      items: prepareBaseCards(content.topCarouselItemsCollection, language),
    },

    statistics: content.facts.factBoxesCollection.items,

    topBillboard: generateBillboard({
      title: content.topBillboard.title,
      image: content.topBillboard.primaryMediaImage,
      preamble: content.topBillboard.richPreamble?.json,
      cta: {
        text: content.topBillboard.tagline || '',
        url: content.topBillboard.teaserUrl || '',
      },
      bottomLinkEntries: content.topBillboard.callToActionCollection.items,
    }),

    campaignCards: prepareBaseCards(content.campaignCardsCollection, language),

    table: generateTable({
      title: content.filteredItemsHeading,
      cta: content.filteredItemsCta,
    }),

    bottomBillboard: generateBillboard({
      title: content.bottomBillboard.title,
      image: content.bottomBillboard.primaryMediaImage,
      preamble: content.bottomBillboard.richPreamble?.json,
      cta: {
        text: content.bottomBillboard.tagline || '',
        url: content.bottomBillboard.teaserUrl || '',
      },
      bottomLinkEntries: content.bottomBillboard.callToActionCollection.items,
    }),

    seoMetadata: generateSeoMetadata(content.seoMetadata || {}),
  };
}
